import { ArrowRight, BadgePercent, ChevronDown, RotateCcw, TrendingDown, TrendingUp } from 'lucide-react';
import {
  buildDiscountPreview, buildMarkupPreview, localizedNumber, money,
  validatePricingSteps,
} from './aluminium-deals/pricing';

export const DEFAULT_PRICING = {
  enabled: false,
  workflowMode: 'discount_only',
  markupMode: 'markups',
  markups: {},
  generalMarkup: '0',
  markupTargetGross: '',
  mode: 'discounts',
  discounts: {},
  generalDiscount: '0',
  targetGross: '',
  vat: '23',
};

export const initialPricing = analysis => ({
  ...DEFAULT_PRICING,
  vat: String(analysis?.vat_rate || '23'),
  markups: Object.fromEntries((analysis?.items || []).map(item => [item.number, '0'])),
  discounts: Object.fromEntries((analysis?.items || []).map(item => [item.number, '0'])),
});

export const evaluatePricing = (analysis, settings) => {
  const markup = buildMarkupPreview({
    analysis, workflowMode: settings.workflowMode, markupMode: settings.markupMode,
    markups: settings.markups, generalMarkup: settings.generalMarkup,
    markupTargetGross: settings.markupTargetGross, vat: settings.vat,
    targetCurrency: '', exchangeRate: '', exchangeRateDirection: 'source_to_target',
  });
  const discount = buildDiscountPreview({
    analysis, workflowMode: settings.workflowMode, markupMode: settings.markupMode,
    markupPreview: markup, mode: settings.mode, discounts: settings.discounts,
    generalDiscount: settings.generalDiscount, targetGross: settings.targetGross,
    vat: settings.vat,
  });
  const validation = validatePricingSteps({
    analysis, convertedOriginalValues: null, markupPreview: markup,
    vat: settings.vat, changeCurrency: false, targetCurrency: '',
    exchangeRate: '', exchangeRateDirection: 'source_to_target',
    markupMode: settings.markupMode, markups: settings.markups,
    generalMarkup: settings.generalMarkup, markupTargetGross: settings.markupTargetGross,
    mode: settings.mode, discounts: settings.discounts,
    generalDiscount: settings.generalDiscount, targetGross: settings.targetGross,
  });
  return {
    markup, discount, validation,
    invalid: settings.enabled && (
      !analysis || validation.discountStepInvalid
      || (settings.workflowMode === 'markup_and_discount' && validation.markupStepInvalid)
    ),
  };
};

const percent = (value, digits = 2) => `${Number(value || 0).toLocaleString('pl-PL', {
  minimumFractionDigits: digits, maximumFractionDigits: digits,
})}%`;

const signedMoney = (value, currency) => {
  const amount = Number(value || 0);
  if (Math.abs(amount) < 0.005) return money(0, currency);
  return `${amount > 0 ? '+' : '−'}${money(Math.abs(amount), currency)}`;
};

const currencySymbol = currency => {
  try {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency', currency: currency === 'EURO' ? 'EUR' : currency,
    }).formatToParts(0).find(part => part.type === 'currency')?.value || currency;
  } catch {
    return currency;
  }
};

const isInvalidNumber = (value, { min = 0, max = Infinity } = {}) => {
  if (String(value ?? '').trim() === '') return true;
  const number = localizedNumber(value);
  return !Number.isFinite(number) || number < min || number > max;
};

function Segmented({ label, options, value, onSelect, disabled }) {
  return <div className="client-offers-segmented" role="group" aria-label={label}>
    {options.map(option => <button key={option.value} type="button"
      className={`client-offers-pricing-choice ${value === option.value ? 'active' : ''}`}
      aria-pressed={value === option.value} disabled={disabled}
      onClick={() => onSelect(option.value)}>{option.label}</button>)}
  </div>;
}

function NumberField({ label, unit, hint, invalid, ...input }) {
  return <label className="client-offers-pricing-field">{label}
    <span className={`client-offers-unit-input ${invalid ? 'invalid' : ''}`}>
      <input type="number" step="0.01" aria-invalid={invalid || undefined} {...input} />
      {unit && <span className="client-offers-unit" aria-hidden="true">{unit}</span>}
    </span>
    {hint && <small>{hint}</small>}
  </label>;
}

function SummaryCard({ title, caption, tone, totals, rate, currency, base }) {
  const change = base ? totals.net - base.net : null;
  const changePercent = base && base.net ? (change / base.net) * 100 : 0;
  const Trend = change > 0 ? TrendingUp : TrendingDown;
  return <div className={`client-offers-summary-card ${tone || ''}`}>
    <div className="client-offers-summary-head"><strong>{title}</strong>{caption && <small>{caption}</small>}</div>
    <div className="client-offers-summary-gross">
      <span>Brutto</span><b>{money(totals.gross, currency)}</b>
    </div>
    <dl>
      <div><dt>Netto</dt><dd>{money(totals.net, currency)}</dd></div>
      <div><dt>VAT {rate}%</dt><dd>{money(totals.vat, currency)}</dd></div>
    </dl>
    {base && <p className={`client-offers-delta ${Math.abs(change) < 0.005 ? 'neutral' : change > 0 ? 'up' : 'down'}`}>
      {Math.abs(change) >= 0.005 && <Trend size={15} aria-hidden="true" />}
      {Math.abs(change) < 0.005
        ? 'Bez zmiany względem oferty pierwotnej'
        : `${signedMoney(change, currency)} netto (${changePercent > 0 ? '+' : '−'}${percent(Math.abs(changePercent))})`}
    </p>}
  </div>;
}

function Step({ number, title, description, children }) {
  return <div className="client-offers-pricing-step">
    <div className="client-offers-pricing-step-head">
      <span className="client-offers-pricing-step-number">{number}</span>
      <div><h5>{title}</h5><p>{description}</p></div>
    </div>
    <div className="client-offers-pricing-step-body">{children}</div>
  </div>;
}

export default function ClientOfferPricingSection({
  analysis, error, settings, evaluation, disabled, onChange, expanded, onToggle,
}) {
  const active = settings.enabled && Boolean(analysis);
  const currency = analysis?.currency || 'EURO';
  const symbol = currencySymbol(currency);
  const withMarkup = settings.workflowMode === 'markup_and_discount';
  const originalRate = analysis?.vat_rate;
  const original = analysis && {
    net: Number(analysis.total_net), vat: Number(analysis.vat), gross: Number(analysis.total_gross),
  };
  const vatInvalid = isInvalidNumber(settings.vat, { min: 0, max: 23 });
  const { validation } = evaluation;

  const setMarkupMode = markupMode => onChange({ markupMode, generalMarkup: '0', markupTargetGross: '' });
  const setDiscountMode = mode => onChange({ mode, generalDiscount: '0', targetGross: '' });
  const resetPrices = () => {
    const base = initialPricing(analysis);
    onChange({
      markupMode: base.markupMode, markups: base.markups, generalMarkup: base.generalMarkup,
      markupTargetGross: base.markupTargetGross, mode: base.mode, discounts: base.discounts,
      generalDiscount: base.generalDiscount, targetGross: base.targetGross,
    });
  };

  const markupTargetTooLow = settings.markupMode === 'target_gross'
    && localizedNumber(settings.markupTargetGross) > 0
    && localizedNumber(settings.markupTargetGross) < validation.sourcePricingGross;
  const discountTargetTooHigh = settings.mode === 'target_gross'
    && localizedNumber(settings.targetGross) > evaluation.markup.gross;

  const totalChange = original && original.net
    ? Math.round((((evaluation.discount.net / original.net) - 1) * 100) * 100) / 100
    : 0;
  const rows = (analysis?.items || []).map((item, index) => ({
    item,
    markup: evaluation.markup.rows[index],
    discount: evaluation.discount.rows[index],
  }));

  return <section className="client-offers-card client-offers-step client-offers-pricing" aria-labelledby="client-offers-pricing-title">
    <div className="client-offers-step-heading">
      <span className="client-offers-number">5</span>
      <span className="client-offers-step-icon"><BadgePercent size={21} aria-hidden="true" /></span>
      <div className="client-offers-step-title"><h4 id="client-offers-pricing-title">Rabatowanie</h4><p>Zmień ceny pozycji i przelicz podsumowanie oferty. Ceny pierwotne zawsze pozostają widoczne.</p></div>
      <label className="client-offers-toggle"><input type="checkbox" aria-label="Zastosuj rabatowanie"
        checked={settings.enabled} disabled={disabled || !analysis}
        onChange={event => onChange({ enabled: event.target.checked })} />
        <span className="client-offers-toggle-track" aria-hidden="true" />
        <span>{settings.enabled ? 'Włączone' : 'Wyłączone'}</span></label>
      <button className={`client-offers-collapse ${expanded ? 'open' : ''}`} type="button"
        aria-label={`${expanded ? 'Zwiń' : 'Rozwiń'} rabatowanie`}
        aria-expanded={expanded} aria-controls="client-offers-pricing-fields" onClick={onToggle}>
        <ChevronDown size={20} aria-hidden="true" />
      </button>
    </div>

    <div id="client-offers-pricing-fields" hidden={!expanded}>
      {!analysis && <p className="client-offers-pricing-error">{error || 'Nie udało się odczytać pozycji i cen z tej oferty.'}</p>}

      {analysis && !settings.enabled && <div className="client-offers-pricing-content client-offers-pricing-idle">
        <SummaryCard title="Oferta pierwotna" caption={`${analysis.items.length} poz. · ${currency === 'EURO' ? 'EUR' : currency}`}
          totals={original} rate={originalRate} currency={currency} />
        <p className="client-offers-pricing-lead">Włącz rabatowanie, aby dodać narzut lub rabat do pozycji albo ustawić docelową kwotę brutto. Zmiany zobaczysz od razu w podsumowaniu i tabeli.</p>
      </div>}

      {active && <div className={`client-offers-pricing-content ${evaluation.invalid ? 'has-errors' : ''}`}>
        <div className={`client-offers-summary ${withMarkup ? 'three' : 'two'}`}>
          <SummaryCard title="Oferta pierwotna" caption="przed zmianami" totals={original}
            rate={originalRate} currency={currency} />
          {withMarkup && <>
            <ArrowRight className="client-offers-summary-arrow" size={18} aria-hidden="true" />
            <SummaryCard title="Po narzucie" caption="krok 1" totals={evaluation.markup}
              rate={settings.vat} currency={currency} base={original} />
          </>}
          <ArrowRight className="client-offers-summary-arrow" size={18} aria-hidden="true" />
          <SummaryCard title="Wynik końcowy" caption={evaluation.invalid ? 'wymaga poprawy danych' : withMarkup ? 'po narzucie i rabacie' : 'po rabacie'}
            tone="result" totals={evaluation.discount} rate={settings.vat} currency={currency} base={original} />
        </div>

        <div className="client-offers-pricing-settings">
          <div className="client-offers-pricing-setting">
            <span className="client-offers-pricing-setting-label">Tryb pracy</span>
            <Segmented label="Tryb rabatowania" disabled={disabled} value={settings.workflowMode}
              onSelect={workflowMode => onChange({ workflowMode })}
              options={[{ value: 'discount_only', label: 'Tylko rabat' }, { value: 'markup_and_discount', label: 'Narzut, potem rabat' }]} />
          </div>
          <NumberField label="VAT (%)" unit="%" min="0" max="23" value={settings.vat} disabled={disabled}
            invalid={vatInvalid} hint={analysis.vat_rate_detected ? `Stawka z oferty: ${analysis.vat_rate}%` : undefined}
            onChange={event => onChange({ vat: event.target.value })} />
        </div>
        {!analysis.vat_rate_detected && <p className="client-offers-pricing-hint">Nie rozpoznano stawki VAT. Sprawdź wartość przed zapisaniem.</p>}

        <div className="client-offers-pricing-steps">
          {withMarkup && <Step number="1" title="Narzut"
            description="Podnosi ceny pierwotne. Rabat z kroku 2 jest liczony od cen po narzucie.">
            <Segmented label="Sposób naliczania narzutu" disabled={disabled} value={settings.markupMode}
              onSelect={setMarkupMode}
              options={[{ value: 'markups', label: 'Dla pozycji' }, { value: 'general_markup', label: 'Ogólny' }, { value: 'target_gross', label: 'Docelowe brutto' }]} />
            {settings.markupMode === 'markups' && <p className="client-offers-pricing-note">Wpisz narzut (%) przy każdej pozycji w tabeli poniżej.</p>}
            {settings.markupMode === 'general_markup' && <NumberField label="Narzut ogólny (%)" unit="%" min="0"
              value={settings.generalMarkup} disabled={disabled}
              invalid={isInvalidNumber(settings.generalMarkup)}
              hint="Ten sam narzut dla wszystkich pozycji."
              onChange={event => onChange({ generalMarkup: event.target.value })} />}
            {settings.markupMode === 'target_gross' && <>
              <NumberField label="Kwota brutto po narzucie" unit={symbol} min="0.01" value={settings.markupTargetGross}
                disabled={disabled} invalid={markupTargetTooLow || isInvalidNumber(settings.markupTargetGross, { min: 0.01 })}
                hint={`Minimum: ${money(validation.sourcePricingGross, currency)} (wartość brutto przed narzutem)`}
                onChange={event => onChange({ markupTargetGross: event.target.value })} />
              {evaluation.markup.automaticPercentage !== null && !markupTargetTooLow
                && <p className="client-offers-pricing-note">Wyliczony narzut: <b>{percent(evaluation.markup.automaticPercentage, 4)}</b></p>}
            </>}
            {markupTargetTooLow && <p className="client-offers-pricing-error">Docelowa kwota musi być wyższa od wartości przed narzutem.</p>}
          </Step>}

          <Step number={withMarkup ? '2' : '1'} title="Rabat"
            description={withMarkup ? 'Obniża ceny po narzucie.' : 'Obniża ceny pierwotne z oferty.'}>
            <Segmented label="Sposób naliczania rabatu" disabled={disabled} value={settings.mode}
              onSelect={setDiscountMode}
              options={[{ value: 'discounts', label: 'Dla pozycji' }, { value: 'general_discount', label: 'Ogólny' }, { value: 'target_gross', label: 'Docelowe brutto' }]} />
            {settings.mode === 'discounts' && <p className="client-offers-pricing-note">Wpisz rabat (%) przy każdej pozycji w tabeli poniżej.</p>}
            {settings.mode === 'general_discount' && <NumberField label="Rabat ogólny (%)" unit="%" min="0" max="100"
              value={settings.generalDiscount} disabled={disabled}
              invalid={isInvalidNumber(settings.generalDiscount, { max: 100 })}
              hint="Ten sam rabat dla wszystkich pozycji."
              onChange={event => onChange({ generalDiscount: event.target.value })} />}
            {settings.mode === 'target_gross' && <>
              <NumberField label="Kwota brutto po rabacie" unit={symbol} min="0.01" value={settings.targetGross}
                disabled={disabled} invalid={discountTargetTooHigh || isInvalidNumber(settings.targetGross, { min: 0.01 })}
                hint={`Maksimum: ${money(evaluation.markup.gross, currency)} (wartość brutto przed rabatem)`}
                onChange={event => onChange({ targetGross: event.target.value })} />
              {evaluation.discount.automaticDiscount !== null && !discountTargetTooHigh
                && <p className="client-offers-pricing-note">Wyliczony rabat: <b>{percent(evaluation.discount.automaticDiscount, 4)}</b></p>}
            </>}
            {discountTargetTooHigh && <p className="client-offers-pricing-error">Docelowa kwota nie może być wyższa od wartości przed rabatem.</p>}
          </Step>
        </div>

        <div className="client-offers-pricing-tablebar">
          <h5>Ceny pozycji <small>(netto za sztukę, {symbol})</small></h5>
          <button type="button" className="client-offers-pricing-reset" onClick={resetPrices} disabled={disabled}>
            <RotateCcw size={15} aria-hidden="true" /> Przywróć ceny pierwotne
          </button>
        </div>
        <div className="client-offers-pricing-table-wrap"><table className="client-offers-pricing-table">
          <caption className="client-offers-sr-only">Porównanie cen pierwotnych i cen po zmianach dla każdej pozycji</caption>
          <thead>
            <tr className="client-offers-pricing-groups">
              <td colSpan="2" />
              <th colSpan="2" scope="colgroup" className="group-original">Oferta pierwotna</th>
              {withMarkup && <th colSpan="2" scope="colgroup" className="group-markup">Krok 1 · Narzut</th>}
              <th colSpan="1" scope="colgroup" className="group-discount">{withMarkup ? 'Krok 2 · ' : ''}Rabat</th>
              <th colSpan="3" scope="colgroup" className="group-result">Wynik końcowy</th>
            </tr>
            <tr>
              <th scope="col">Poz.</th><th scope="col">Ilość</th>
              <th scope="col" className="group-original">Cena</th><th scope="col" className="group-original">Wartość</th>
              {withMarkup && <><th scope="col" className="group-markup">Narzut</th><th scope="col" className="group-markup">Cena po narzucie</th></>}
              <th scope="col" className="group-discount">Rabat</th>
              <th scope="col" className="group-result">Cena</th><th scope="col" className="group-result">Wartość</th><th scope="col" className="group-result">Zmiana</th>
            </tr>
          </thead>
          <tbody>{rows.map(({ item, markup, discount }) => {
            const originalUnit = Number(item.unit_net);
            const finalUnit = Number(discount.newUnit);
            const change = originalUnit ? ((finalUnit / originalUnit) - 1) * 100 : 0;
            const unchanged = Math.abs(finalUnit - originalUnit) < 0.005;
            return <tr key={item.number}>
              <td>{item.number}</td><td>{item.quantity}</td>
              <td className="group-original">{money(originalUnit, currency)}</td>
              <td className="group-original">{money(item.value_net, currency)}</td>
              {withMarkup && <>
                <td className="group-markup">{settings.markupMode === 'markups'
                  ? <span className="client-offers-unit-input compact"><input type="number" min="0" step="0.01"
                    aria-label={`Narzut pozycji ${item.number}`} value={settings.markups[item.number] ?? '0'} disabled={disabled}
                    aria-invalid={isInvalidNumber(settings.markups[item.number] ?? '0') || undefined}
                    onChange={event => onChange({ markups: { ...settings.markups, [item.number]: event.target.value } })} /><span className="client-offers-unit" aria-hidden="true">%</span></span>
                  : percent(markup.adjustment, 4)}</td>
                <td className="group-markup">{money(markup.priceUnit, currency)}</td>
              </>}
              <td className="group-discount">{settings.mode === 'discounts'
                ? <span className="client-offers-unit-input compact"><input type="number" min="0" max="100" step="0.01"
                  aria-label={`Rabat pozycji ${item.number}`} value={settings.discounts[item.number] ?? '0'} disabled={disabled}
                  aria-invalid={isInvalidNumber(settings.discounts[item.number] ?? '0', { max: 100 }) || undefined}
                  onChange={event => onChange({ discounts: { ...settings.discounts, [item.number]: event.target.value } })} /><span className="client-offers-unit" aria-hidden="true">%</span></span>
                : percent(discount.discount, 4)}</td>
              <td className="group-result strong">{money(finalUnit, currency)}</td>
              <td className="group-result strong">{money(discount.newValue, currency)}</td>
              <td className="group-result"><span className={`client-offers-delta-chip ${unchanged ? 'neutral' : change > 0 ? 'up' : 'down'}`}>
                {unchanged ? '—' : `${change > 0 ? '+' : '−'}${percent(Math.abs(change))}`}</span></td>
            </tr>;
          })}</tbody>
          <tfoot><tr>
            <th scope="row" colSpan="3">Razem netto</th>
            <td className="group-original">{money(original.net, currency)}</td>
            {withMarkup && <><td className="group-markup" /><td className="group-markup" /></>}
            <td className="group-discount" />
            <td className="group-result" />
            <td className="group-result strong">{money(evaluation.discount.net, currency)}</td>
            <td className="group-result"><span className={`client-offers-delta-chip ${totalChange === 0 ? 'neutral' : totalChange > 0 ? 'up' : 'down'}`}>
              {totalChange === 0 ? '—' : `${totalChange > 0 ? '+' : '−'}${percent(Math.abs(totalChange))}`}</span></td>
          </tr></tfoot>
        </table></div>

        {evaluation.invalid && <p className="client-offers-pricing-error" role="alert">Popraw zaznaczone pola (narzut, rabat lub VAT), aby przygotować PDF.</p>}
      </div>}
    </div>
  </section>;
}
